import Tooltip from '@mui/material/Tooltip';
import { alpha, useTheme } from '@mui/material/styles';
import { memo, UIEvent, useMemo, useRef, useState } from 'react';

const LINE_HEIGHT = 18;
const FONT_SIZE = 12;
const PAD_Y = 12;
const PAD_X = 12;

type Props = {
  value: string;
  onChange: (value: string) => void;
  error?: boolean;
  /** 1-based line to highlight in the gutter (e.g. from a JSON parse error). */
  errorLine?: number;
  /** Visible height in rows (ignored when `fillHeight` is set). */
  rows?: number;
  /** Fill the parent's height instead of a fixed row count. The parent must
   *  have a resolved height (e.g. a flex child with `flex: 1; min-height: 0`). */
  fillHeight?: boolean;
  placeholder?: string;
  ariaLabel?: string;
  readOnly?: boolean;
  /** 1-based line -> hover note. Only honoured in `readOnly` mode, which renders
   *  rows instead of a textarea; annotated rows get a warning tint + tooltip. */
  lineNotes?: ReadonlyMap<number, string>;
};

/**
 * A lightweight code/JSON textarea with a synced line-number gutter — no extra
 * dependency. Uses `white-space: pre` (no wrapping) so each logical line maps to
 * exactly one row, keeping the gutter aligned and matching the "line N" numbers
 * in JSON parse errors. A given `errorLine` is highlighted in the gutter.
 *
 * `readOnly` swaps the textarea for a **viewer** that renders one element per
 * line. A textarea can't carry per-line backgrounds or hover targets, and the
 * viewer is what makes `lineNotes` possible; text stays selectable either way.
 */
export const JsonEditor = memo(
  ({
    value,
    onChange,
    error,
    errorLine,
    rows = 18,
    fillHeight = false,
    placeholder,
    ariaLabel = 'JSON editor',
    readOnly = false,
    lineNotes,
  }: Props) => {
    const theme = useTheme();
    const gutterRef = useRef<HTMLDivElement>(null);
    const [focused, setFocused] = useState(false);

    const lines = useMemo(() => value.split('\n'), [value]);

    const handleScroll = (e: UIEvent<HTMLElement>) => {
      if (gutterRef.current) {
        gutterRef.current.scrollTop = e.currentTarget.scrollTop;
      }
    };

    const borderColor = error
      ? theme.palette.error.main
      : focused
      ? theme.palette.primary.main
      : theme.palette.grey[400];

    const monospace = {
      fontFamily: 'monospace',
      fontSize: FONT_SIZE,
      lineHeight: `${LINE_HEIGHT}px`,
    } as const;

    const noteBackground = alpha(theme.palette.warning.main, 0.18);

    return (
      <div
        style={{
          display: 'flex',
          height: fillHeight ? '100%' : rows * LINE_HEIGHT + PAD_Y * 2,
          border: `1px solid ${borderColor}`,
          borderRadius: theme.shape.borderRadius,
          overflow: 'hidden',
          background: theme.palette.background.paper,
          boxShadow:
            focused && !error
              ? `0 0 0 1px ${theme.palette.primary.main}`
              : 'none',
        }}
      >
        <div
          ref={gutterRef}
          aria-hidden
          style={{
            ...monospace,
            flexShrink: 0,
            padding: `${PAD_Y}px 8px`,
            textAlign: 'right',
            color: theme.palette.text.disabled,
            background: theme.palette.grey[50],
            userSelect: 'none',
            overflow: 'hidden',
            minWidth: 34,
          }}
        >
          {lines.map((_, i) => {
            const n = i + 1;
            const annotated = lineNotes?.has(n);
            return (
              <div
                key={n}
                style={{
                  height: LINE_HEIGHT,
                  color:
                    n === errorLine
                      ? theme.palette.error.main
                      : annotated
                      ? theme.palette.warning.dark
                      : undefined,
                  fontWeight: n === errorLine || annotated ? 700 : undefined,
                }}
              >
                {n}
              </div>
            );
          })}
        </div>

        {readOnly ? (
          <div
            role="region"
            aria-label={ariaLabel}
            tabIndex={0}
            onScroll={handleScroll}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            style={{
              ...monospace,
              flex: 1,
              padding: `${PAD_Y}px ${PAD_X}px`,
              color: theme.palette.text.primary,
              overflow: 'auto',
              whiteSpace: 'pre',
              outline: 'none',
            }}
          >
            {lines.map((line, i) => {
              const note = lineNotes?.get(i + 1);
              const row = (
                <div
                  key={i}
                  style={{
                    height: LINE_HEIGHT,
                    // Fill the scroll width so the tint doesn't stop short on
                    // long lines, but never shrink below the visible box.
                    width: 'max-content',
                    minWidth: '100%',
                    background: note ? noteBackground : undefined,
                    boxShadow: note
                      ? `inset 2px 0 0 ${theme.palette.warning.main}`
                      : undefined,
                    cursor: note ? 'help' : undefined,
                  }}
                >
                  {line === '' ? ' ' : line}
                </div>
              );

              return note ? (
                <Tooltip
                  key={i}
                  title={note}
                  placement="top-start"
                  disableInteractive
                  enterDelay={200}
                >
                  {row}
                </Tooltip>
              ) : (
                row
              );
            })}
          </div>
        ) : (
          <textarea
            aria-label={ariaLabel}
            placeholder={placeholder}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onScroll={handleScroll}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            spellCheck={false}
            autoCorrect="off"
            autoCapitalize="off"
            autoComplete="off"
            style={{
              ...monospace,
              flex: 1,
              margin: 0,
              border: 0,
              outline: 'none',
              resize: 'none',
              padding: `${PAD_Y}px ${PAD_X}px`,
              background: 'transparent',
              color: theme.palette.text.primary,
              overflow: 'auto',
              whiteSpace: 'pre',
            }}
          />
        )}
      </div>
    );
  }
);

JsonEditor.displayName = 'JsonEditor';
